// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useCallback, useState, useEffect } from "react";
import { useVaccineDashboardStore, __dashboardStoreInternals } from "../../../store/vaccineDashboardStore";
import { useCreateScenario } from "../hooks/useCreateScenario";
import { VaccineDashboardHeader } from "./VaccineDashboardHeader";
import { logActivity } from "../lib/storage";
import { exportAndDownloadJSON } from "../../../lib/export";
import { useVaccineClientStore } from "../../../store/vaccineClientStore";
import { importAndUploadJSON } from "../../../lib/import";
import { ScenarioManagerModal } from "./ScenarioManagerModal";
import { ExportImportModal } from "./ExportImportModal";
import { useAlerts } from "../../../hooks/useAlerts";
import { syncVaccineParametersToTree, syncTreeParametersToVaccine } from "../../../facade/vaccineFacade";


export const VaccineDashboardHeaderConnected: React.FC = () => {
  const disease = useVaccineDashboardStore((state) => state.disease);
  const scenarios = useVaccineDashboardStore((state) => state.scenarios);
  const activity = useVaccineDashboardStore((state) => state.activity);

  // DEBUG: Track disease changes to verify component re-renders on loadScenario
  useEffect(() => {
    console.log('[HeaderConnected] disease changed:', disease);
  }, [disease]);

  const setDisease = useVaccineDashboardStore((state) => state.setDisease);
  const resetToDefaults = useVaccineDashboardStore((state) => state.resetToDefaults);

  // Decision Tree Store
  const treeStore = useVaccineClientStore();

  // Alert system
  const { showSuccess, showError, showWarning } = useAlerts();

  const handleDiseaseChange = useCallback(
    (value: string) => {
      setDisease(value);
    },
    [setDisease]
  );

  // Force-commit any pending NumberField edits before reading the store
  const commitActiveField = useCallback(() => {
    const el = document.activeElement as HTMLElement | null;
    el?.blur(); // triggers NumberField onBlur commit path
  }, []);

  const createScenarioCallback = useCreateScenario();
  const handleAddScenario = useCallback(() => {
    commitActiveField();
    // Delay to ensure blur handlers have executed and debounced updates are flushed
    setTimeout(() => {
      createScenarioCallback();
    }, 100);
  }, [commitActiveField, createScenarioCallback]);

  // --- EXPORT LOGIC ---
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isScenarioManagerOpen, setIsScenarioManagerOpen] = useState(false);

  const handleExportClick = useCallback(() => {
    if (scenarios.length === 0) {
      console.warn("No scenarios available to download");
      showWarning("Please create a scenario before exporting.");
      return;
    }
    setIsExportModalOpen(true);
  }, [scenarios, showWarning]);

  const handleExportSelect = useCallback(async () => {
    commitActiveField();
    // Wait for blur handlers to execute and debounced updates to flush before
    // snapshotting the stores. We return a Promise so the modal can show an
    // in-flight state and close itself once the action completes.
    await new Promise<void>((resolve) => setTimeout(resolve, 100));

    try {
      if (scenarios.length === 0) return;

      // Extract decisionTree data from store, including parameters
      const treeState = treeStore.getTreeState();
      const decisionTree = {
        nodes: treeState.nodes,
        edges: treeState.edges,
        rootId: treeState.rootId,
        // Include parameter groups at top level (matching fixture structure)
        costParameters: treeStore.costParameters,
        populationParameters: treeStore.populationParameters,
        modelMetadata: treeStore.modelMetadata,
        simulationParameters: treeStore.simulationParameters,
      };

      // Export all scenarios as JSON (the single, lossless format)
      exportAndDownloadJSON(scenarios, decisionTree, activity);
      logActivity("Exported all scenarios to JSON");
    } finally {
      setIsExportModalOpen(false);
    }
  }, [scenarios, treeStore, activity, commitActiveField]);

  const [statusMessage] = useState<string | null>(null);

  const handleImportClick = useCallback(() => {
    setIsImportModalOpen(true);
  }, []);

  const handleImportSelect = useCallback(async () => {
    // Set import hydration guard flags to prevent auto-computation from interfering
    useVaccineDashboardStore.setState({ isImporting: true });
    useVaccineClientStore.setState({ isImporting: true });

    try {
      const data: any = await importAndUploadJSON();

      if (!data) {
        return;
      }

      // A) Apply imported dashboard inputs first (may include old/default values like efficacy=65)
      const scenarioFromArray = data.scenarios && data.scenarios.length > 0 ? data.scenarios[0] : null;
      const primaryScenario = data.scenario || scenarioFromArray;
      
      // ✅ Robust decisionTree resolution for old/new export formats
      const dt =
        (data as any).decisionTree ??
        (primaryScenario as any)?.decisionTree ??
        (primaryScenario as any)?.tree ??
        null;
      
      const importedInputs = data.inputs ?? (scenarioFromArray && 'inputs' in scenarioFromArray ? scenarioFromArray.inputs : undefined);
      
      const dashboardStore = useVaccineDashboardStore.getState();
      const treeStore = useVaccineClientStore.getState();
      
      if (importedInputs) {
        // Apply imported inputs immutably (may include old/default efficacy=65)
        // Then rebuild state to ensure nationalCoveragePct is properly derived
        useVaccineDashboardStore.setState((s) => {
          const updatedInputs = { ...s.inputs, ...importedInputs };
          // Rebuild state from inputs to ensure nationalCoveragePct is properly derived
          const { convertInputsToState, normalizeSelectedRegions, deriveInputsFromState } = __dashboardStoreInternals;
          const { globalInputs, regional } = convertInputsToState(updatedInputs);
          
          // Normalize regional selection
          const normalizedRegions = normalizeSelectedRegions(regional.selectedRegions);
          const nextSelected = 
            regional.selectedRegionId && normalizedRegions.includes(regional.selectedRegionId)
              ? regional.selectedRegionId
              : normalizedRegions[0] ?? 'finland';
          
          const normalizedRegional = {
            ...regional,
            selectedRegions: normalizedRegions,
            selectedRegionId: nextSelected,
          };
          
          // Derive final inputs from rebuilt state
          const derivedInputs = deriveInputsFromState(globalInputs, normalizedRegional);
          
          return {
            ...s,
            inputs: derivedInputs,
            globalInputs,
            regional: normalizedRegional,
          };
        });
      }
      
      // Set disease and audience if available
      if (primaryScenario?.disease) {
        dashboardStore.setDisease(primaryScenario.disease);
      }
      if (primaryScenario?.audience) {
        dashboardStore.setAudience(primaryScenario.audience);
      }
      
      // Add activity log entry
      const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const scenarioName = primaryScenario?.name.toUpperCase() || 'IMPORTED SCENARIO';
      const newLogEntry = {
        text: `Imported scenario: ${scenarioName} • ${currentTime}`,
        timestamp: Date.now(),
      };
      dashboardStore.setActivity([newLogEntry, ...(data.activity || [])]);
      
      // If import has multiple scenarios, add them to the list
      if (data.scenarios && data.scenarios.length > 0) {
        useVaccineDashboardStore.setState((state) => ({
          scenarios: [...data.scenarios!, ...state.scenarios].slice(0, 20),
        }));
      }

      // B) Hydrate tree (graph + parameter blocks including costParameters.efficacy)
      if (dt) {
        if (dt.nodes) {
          treeStore.setNodes(dt.nodes);
        }
        if (dt.edges) {
          treeStore.setEdges(dt.edges);
        }
        if (dt.rootId !== undefined && dt.rootId !== null) {
          treeStore.setRootId(dt.rootId);
        }
        // Hydrate parameter blocks
        if (dt.costParameters) {
          treeStore.setCostParameters(dt.costParameters);
        }
        if (dt.populationParameters) {
          treeStore.setPopulationParameters(dt.populationParameters);
        }
        if (dt.modelMetadata) {
          treeStore.setModelMetadata(dt.modelMetadata);
        }
        if (dt.simulationParameters) {
          // willingness_to_pay is no longer persisted. Strip it from any older
          // export that still carries it so the store keeps its internal default
          // (setSimulationParameters merges over current state).
          const { willingness_to_pay: _ignoredWtp, ...simParams } = dt.simulationParameters;
          treeStore.setSimulationParameters(simParams);
        }
      }

      // C) FINAL: Overwrite dashboard from tree params (tree is canonical, must include efficacy)
      // This ensures tree parameters (e.g., efficacy=0.77) override any old/default values (e.g., efficacy=65) in imported inputs
      syncTreeParametersToVaccine();

      showSuccess('Scenario imported successfully!');
      logActivity(`Imported data: ${data.hash}`);
    } catch (error) {
      console.error("Import failed", error);
      
      if (error instanceof Error) {
        if (error.message.includes('Hash verification failed')) {
          showError(
            'The file has been tampered with or corrupted. Please ensure you are importing an unmodified export file.',
            'Hash Verification Failed'
          );
        } else if (error.message.includes('JSON')) {
          showError(
            'The file format is invalid or corrupted. Please check the file and try again.',
            'Invalid File Format'
          );
        } else {
          showError(error.message, 'Import Failed');
        }
      } else {
        showError('An unexpected error occurred during import.', 'Import Failed');
      }
    } finally {
      // Clear import hydration guard flags
      useVaccineDashboardStore.setState({ isImporting: false });
      useVaccineClientStore.setState({ isImporting: false });
      setIsImportModalOpen(false);
    }
  }, [showSuccess, showError]);

  return (
    <>
      {/* Alert Container removed to prevent duplicates (rendered in BackendComputationIndicator) */}
      
      {/* Legacy status message (can be removed once alerts are fully integrated) */}
      {statusMessage && (
        <div
          style={{
            position: "fixed",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "10px 20px",
            background: "#333",
            color: "#fff",
            borderRadius: "4px",
            zIndex: 9999,
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          }}
        >
          {statusMessage}
        </div>
      )}
      
      <ExportImportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onSelect={handleExportSelect}
        title="Export Scenarios"
        message={`You have ${scenarios.length} saved scenarios. They will be exported as a JSON file.`}
      />

      <ExportImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSelect={handleImportSelect}
        title="Import Scenario"
        message="Select a previously exported JSON file to import."
      />

      <ScenarioManagerModal
        isOpen={isScenarioManagerOpen}
        onClose={() => setIsScenarioManagerOpen(false)}
      />

      <VaccineDashboardHeader
        disease={disease}
        handleDiseaseChange={handleDiseaseChange}
        addScenario={handleAddScenario}
        handleExport={handleExportClick}
        handleImport={handleImportClick}
        onOpenScenarioManager={() => setIsScenarioManagerOpen(true)}
        scenarioCount={scenarios.length}
        onReset={() => {
          resetToDefaults();
          treeStore.reset();
          // Clear validation issues before syncing to avoid showing stale errors
          // The reset() call above triggers validation, but we'll sync and re-validate
          const resetInputs = useVaccineDashboardStore.getState().inputs;
          // Sync tree structure with reset dashboard inputs to ensure infected nodes
          // are properly converted to chance nodes with correct probabilities
          // Note: syncVaccineParametersToTree calls setNodes/setEdges which trigger validation automatically
          syncVaccineParametersToTree(resetInputs);
          logActivity("Reset all (dashboard + tree)");
        }}
      />
    </>
  );
};
